import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faBoxOpen,
  faFileExcel,
  faRocket,
} from "@fortawesome/pro-light-svg-icons";
import styled from "styled-components";

import { getProduto, getProductInterested } from "../../../../api/produtos";
import { exportToExcel } from "../../../../utils/excel";

function Interessados() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tipo = searchParams.get("tipo") === "notify" ? "notify" : "preorder";

  const [produto, setProduto] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ preorder: null, notify: null });
  const [loading, setLoading] = useState(true);

  window.document.title = "Organnact - Admin - Interessados";

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getProduto(id),
      getProductInterested(id, "preorder"),
      getProductInterested(id, "notify"),
    ])
      .then(([p, pre, not]) => {
        if (!active) return;
        setProduto(p);
        setCounts({ preorder: pre?.length || 0, notify: not?.length || 0 });
        setRows(tipo === "preorder" ? pre || [] : not || []);
      })
      .catch(() => active && setRows([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    getProductInterested(id, tipo)
      .then((list) => active && setRows(list || []))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
  }, [tipo, id]);

  function changeTipo(novoTipo) {
    setSearchParams({ tipo: novoTipo });
  }

  function baixarExcel() {
    if (rows.length === 0) return;
    const data = rows.map((r) => ({
      Nome: r.user_name || "",
      Email: r.user_email || "",
      Telefone: r.user_phone || "",
      "Data inscricao": r.created_at
        ? new Date(r.created_at).toLocaleString("pt-BR")
        : "",
    }));
    const tipoLabel = tipo === "preorder" ? "pre-venda" : "aviso";
    exportToExcel(
      data,
      `interessados-${produto?.id || id}-${tipoLabel}.xlsx`,
      tipoLabel
    );
  }

  const totalAtual = rows.length;
  const labelAba = tipo === "preorder" ? "pré-venda" : "aviso";

  const thumb = useMemo(() => {
    const imgs = produto?.imagens || [];
    return imgs[0] || null;
  }, [produto]);

  return (
    <Wrap>
      <Breadcrumb>
        <a onClick={() => navigate("/admin/produtos")}>Produtos</a>
        <span>&nbsp;&gt;&nbsp;</span>
        <strong>{produto?.nome || "Carregando..."}</strong>
      </Breadcrumb>

      <ProdutoStrip>
        <ProdutoThumb>
          {thumb ? (
            <img src={thumb} alt={produto?.nome} />
          ) : (
            <FontAwesomeIcon icon={faBoxOpen} color="#ccc" />
          )}
        </ProdutoThumb>
        <ProdutoMeta>
          <ProdutoNome>{produto?.nome || ""}</ProdutoNome>
          <ProdutoSub>
            {produto?.id_everest ? `#${produto.id_everest} · ` : ""}
            Interessados neste produto
          </ProdutoSub>
        </ProdutoMeta>
        <BaixarBtn onClick={baixarExcel} disabled={totalAtual === 0}>
          <FontAwesomeIcon icon={faFileExcel} /> Baixar Excel
        </BaixarBtn>
      </ProdutoStrip>

      <ResultsHeader>
        <ResultsCount>
          <strong>{totalAtual}</strong>{" "}
          {totalAtual === 1 ? "pessoa" : "pessoas"} em {labelAba}
        </ResultsCount>
        <Segmented role="tablist">
          <Segment
            $active={tipo === "preorder"}
            onClick={() => changeTipo("preorder")}
          >
            <FontAwesomeIcon icon={faRocket} /> Pré-venda
            {counts.preorder !== null && (
              <Pill>{counts.preorder}</Pill>
            )}
          </Segment>
          <Segment
            $active={tipo === "notify"}
            onClick={() => changeTipo("notify")}
          >
            <FontAwesomeIcon icon={faBell} /> Aviso
            {counts.notify !== null && <Pill>{counts.notify}</Pill>}
          </Segment>
        </Segmented>
      </ResultsHeader>

      <Card>
        {loading ? (
          <EmptyState>
            <Spinner />
          </EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>
            <FontAwesomeIcon
              icon={tipo === "preorder" ? faRocket : faBell}
              style={{ fontSize: 32, color: "#ccc", marginBottom: 12 }}
            />
            <div style={{ color: "#666", fontSize: 14 }}>
              Nenhuma pessoa em {labelAba} ainda.
            </div>
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th style={{ width: 200 }}>Data inscrição</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <PersonName>{r.user_name || "—"}</PersonName>
                    </td>
                    <td>
                      {r.user_email ? (
                        <EmailLink href={`mailto:${r.user_email}`}>
                          {r.user_email}
                        </EmailLink>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{r.user_phone || "—"}</td>
                    <td style={{ color: "#999", fontSize: 13 }}>
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString("pt-BR")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </Wrap>
  );
}

const Wrap = styled.div``;

const Breadcrumb = styled.div`
  font-size: 12px;
  color: #999;
  margin-bottom: 12px;

  a {
    color: #3483fa;
    cursor: pointer;
  }
  a:hover {
    text-decoration: underline;
  }
  strong {
    color: #333;
    font-weight: 500;
  }
`;

const ProdutoStrip = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const ProdutoThumb = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProdutoMeta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ProdutoNome = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const ProdutoSub = styled.div`
  font-size: 12px;
  color: #999;
`;

const BaixarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #3483fa;
  border: 1px solid #3483fa;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    background: #3483fa;
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
`;

const ResultsCount = styled.div`
  font-size: 14px;
  color: #666;

  strong {
    color: #333;
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  padding: 3px;
  gap: 2px;
`;

const Segment = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: ${(p) => (p.$active ? "#3483fa" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "#666")};
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${(p) => (p.$active ? "#2968c8" : "#f5f5f5")};
  }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
  font-size: 11px;
  font-weight: 700;
  margin-left: 2px;

  ${Segment}:not([data-active]) & {
    background: #eee;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0;

  thead th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 14px 20px;
    border-bottom: 1px solid #eee;
    background: #fafafa;
  }

  tbody td {
    padding: 14px 20px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
    color: #333;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #fafbfc;
  }
`;

const PersonName = styled.div`
  font-weight: 500;
  color: #333;
`;

const EmailLink = styled.a`
  color: #3483fa;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

export default Interessados;
